import { useEffect, useState } from "react";
import { Plus, Loader2, Edit2, Trash2, Wallet, CreditCard, PiggyBank, Coins, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Tip from "../components/Tip";

const TYPES = [
  ["checking", "Conto corrente", Landmark],
  ["savings", "Risparmio", PiggyBank],
  ["credit_card", "Carta di credito", CreditCard],
  ["investment", "Investimento", Coins],
  ["cash", "Contanti", Wallet],
  ["other", "Altro", Wallet],
];
const TYPE_LABEL = Object.fromEntries(TYPES.map(([v, l]) => [v, l]));
const TYPE_ICON = Object.fromEntries(TYPES.map(([v, , I]) => [v, I]));

const eur = (n, cur = "EUR") => new Intl.NumberFormat("it-IT", { style: "currency", currency: cur }).format(Number(n || 0));

export default function FinancesAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try { setRows(await api.financesListAccounts()); }
    catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (a) => { setEditing(a); setFormOpen(true); };
  const del = async (a) => {
    if (!confirm(`Elimina conto "${a.name}"?`)) return;
    try { await api.financesDeleteAccount(a.id); toast.success("Conto eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const total = rows.filter((a) => a.is_active).reduce((s, a) => s + Number(a.balance_current || 0), 0);

  return (
    <div className="space-y-6" data-testid="finances-accounts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Finanze · Conti</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Conti bancari</h1>
          <p className="mt-2 text-sm opacity-80">
            Saldo totale attivo: <b>{eur(total)}</b>
          </p>
        </div>
        <Tip label="Aggiungi un nuovo conto">
          <button onClick={openNew} className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="accounts-new-btn">
            <Plus size={14} /> Nuovo conto
          </button>
        </Tip>
      </div>

      {loading ? <div className="opacity-70 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Caricamento…</div>
       : err ? <div className="pxos-card p-4" style={{ color: "#B42318" }}>{err}</div>
       : rows.length === 0 ? (
         <div className="pxos-card p-10 text-center opacity-70" data-testid="accounts-empty">
           <div className="mb-3">Nessun conto configurato.</div>
           <button onClick={openNew} className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="accounts-empty-new-btn"><Plus size={14}/> Aggiungi il primo conto</button>
         </div>
       ) : (
         <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="accounts-list">
           {rows.map((a) => {
             const Icon = TYPE_ICON[a.type] || Wallet;
             return (
               <li key={a.id} className={`pxos-card p-4 flex items-center gap-3 ${!a.is_active ? "opacity-60" : ""}`} data-testid="account-row">
                 <div className="h-10 w-10 rounded-full grid place-items-center" style={{ background: "var(--pxos-primary-light)" }}>
                   <Icon size={18} style={{ color: "var(--pxos-primary-accent)" }} />
                 </div>
                 <div className="flex-1 min-w-0">
                   <div className="font-medium truncate" style={{ color: "var(--pxos-text-dark)" }}>{a.name}</div>
                   <div className="text-xs opacity-70">{TYPE_LABEL[a.type] || a.type} {!a.is_active && "· inattivo"}</div>
                 </div>
                 <div className="text-right">
                   <div className="font-display font-semibold" style={{ color: "var(--pxos-text-dark)" }}>{eur(a.balance_current, a.currency)}</div>
                 </div>
                 <div className="flex items-center gap-1 shrink-0">
                   <Tip label="Modifica conto"><button onClick={() => openEdit(a)} className="p-2 rounded-full hover:bg-black/5" data-testid="account-edit"><Edit2 size={14}/></button></Tip>
                   <Tip label="Elimina conto"><button onClick={() => del(a)} className="p-2 rounded-full hover:bg-red-50" data-testid="account-delete"><Trash2 size={14} className="text-red-500"/></button></Tip>
                 </div>
               </li>
             );
           })}
         </ul>
       )}

      <AccountForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} onSaved={() => { load(); setFormOpen(false); }} />
    </div>
  );
}

function AccountForm({ open, onClose, onSaved, initial }) {
  const empty = { name: "", type: "checking", balance_current: 0, currency: "EUR", is_active: true, notes: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { name: initial.name, type: initial.type, balance_current: initial.balance_current, currency: initial.currency, is_active: initial.is_active, notes: initial.notes || "" } : empty);
    // eslint-disable-next-line
  }, [open, initial]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { ...f, balance_current: parseFloat(f.balance_current) || 0 };
    try {
      if (initial) await api.financesUpdateAccount(initial.id, payload);
      else await api.financesCreateAccount(payload);
      toast.success(initial ? "Conto aggiornato" : "Conto creato");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose} data-testid="account-form">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-labelledby="account-form-title"
            className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
          <h3 id="account-form-title" className="font-display text-xl font-semibold">{initial ? "Modifica conto" : "Nuovo conto"}</h3>
          <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="account-form-close">✕</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Nome</span>
            <input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="pxos-input" data-testid="account-name" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Tipo</span>
              <select value={f.type} onChange={(e) => setF({...f, type: e.target.value})} className="pxos-input" data-testid="account-type">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Saldo attuale €</span>
              <input type="number" step="0.01" value={f.balance_current} onChange={(e) => setF({...f, balance_current: e.target.value})} className="pxos-input" data-testid="account-balance" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_active} onChange={(e) => setF({...f, is_active: e.target.checked})} data-testid="account-active" />
            Conto attivo (incluso nei calcoli del dashboard)
          </label>
          <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Note</span>
            <textarea rows="2" value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})} className="pxos-input" data-testid="account-notes" /></label>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--pxos-border)" }}>
          <button type="button" onClick={onClose} className="rounded-full border px-4 py-2 text-xs font-semibold" data-testid="account-cancel">Annulla</button>
          <button type="submit" disabled={busy} className="pxos-btn-primary text-xs disabled:opacity-50" data-testid="account-save">
            {busy ? "Salvataggio…" : (initial ? "Salva modifiche" : "Crea conto")}
          </button>
        </div>
      </form>
    </div>
  );
}
